<script setup lang="ts">

import TheBottomSheet from '@/components/TheBottomSheet.vue'
import BaseButton from '@/components/base/BaseButton.vue'
import type { PlayerTapConfig } from '@/types/shared/player-config'
import { getLevelAssetUrl } from '@/helpers/game'
import { T } from '@tolgee/vue'
import { useAnalytics } from '@/composables/analytics'
import { watch } from 'vue'
import { useConfig } from '@/composables/game/config'
import TwitterIcon from '@/components/icons/TwitterIcon.vue'
import webapp from '@/services/webapp'
import { twitterPostLink } from '@/config/twitter'
import { useGiveaway } from '@/composables/game/giveaway'

const props = defineProps<{
  show: boolean
  currentLevel: number
  tapConfig: PlayerTapConfig
}>()
defineEmits(['hide'])

const { triggerEvent } = useAnalytics()
const { config } = useConfig()

const { countdown: giveawayCountdown } = useGiveaway()

function shareOnX() {
  webapp.openLink(twitterPostLink + '?type=levelup')
}

watch(() => props.show, show => {
  if (!show) return
  triggerEvent({ event: 'level_up', metadata: { level: props.currentLevel } })
})
</script>

<template>
  <TheBottomSheet
    :show="show"
    @hide="$emit('hide')"
  >
    <div class="w-full flex flex-col items-center">
      <div class="circle-ring size-[120px] rounded-full flex justify-center items-center p-[2px] bg-opacity-60">
        <div class="circle relative w-full h-full rounded-full bg-dark-bottom-layer">
          <img
            :src="getLevelAssetUrl(currentLevel)"
            alt="Level image"
            class="h-[144px] w-[144px] min-w-[144px] absolute -translate-y-1/2 -translate-x-1/2 top-1/2 left-1/2"
          >
        </div>
      </div>
      <p class="mt-6 text-2xl font-bold -tracking-[0.48px] uppercase text-light-white">
        <T
          key-name="level_up.modal.title"
          default-value="Congrats, you leveled up!"
        />
      </p>
      <p class="mt-6 text-light-white/70 text-base font-medium -tracking-[0.32px]">
        <T
          key-name="level_up.modal.subtitle"
          default-value="Perks unlocked:"
        />
      </p>
      <div class="w-full mt-3 flex items-center justify-between gap-x-4 max-w-[316px]">
        <div class="flex flex-col items-center justify-center gap-y-1">
          <span class="text-white/60 text-sm font-medium -tracking-[0.28px]">
            <T
              key-name="level_up.modal.multitap"
              default-value="Multitap"
            />
          </span>
          <div class="flex items-center justify-center gap-x-1.5">
            <img
              src="@/assets/images/emojis/emoji-hands-clap.png"
              class="size-[28px]"
            >
            <span class="text-white font-semibold text-base leading-[22px] -tracking-[0.32px]">
              +{{ tapConfig.tap.earnPerTapDelta }}
            </span>
          </div>
        </div>
        <div class="flex flex-col items-center justify-center gap-y-1">
          <span class="text-white/60 text-sm font-medium -tracking-[0.28px]">
            <T
              key-name="level_up.modal.energy_limit"
              default-value="Energy Limit"
            />
          </span>
          <div class="flex w-full items-center justify-center gap-x-1.5">
            <img
              src="@/assets/images/emojis/emoji-lightning.png"
              class="size-[28px]"
            >
            <span class="text-white font-semibold text-base leading-[22px] -tracking-[0.32px]">
              +{{ tapConfig.energy.limitDelta }}
            </span>
          </div>
        </div>
        <div
          v-if="giveawayCountdown"
          class="flex flex-col items-center justify-center gap-y-1"
        >
          <span class="text-white/60 text-sm font-medium -tracking-[0.28px]">
            <T
              key-name="level_up.modal.tickets"
              default-value="Tickets"
            />
          </span>
          <div class="flex w-full items-center justify-center gap-x-1.5">
            <img
              src="@/assets/images/emojis/emoji-ticket.png"
              class="size-[28px]"
            >
            <span class="text-white font-semibold text-base leading-[22px] -tracking-[0.32px]">
              +1
            </span>
          </div>
        </div>
        <div class="flex flex-col items-center justify-center gap-y-1">
          <span class="text-white/60 text-sm font-medium -tracking-[0.28px]">
            <T
              key-name="level_up.modal.spins"
              default-value="Spins"
            />
          </span>
          <div class="flex w-full items-center justify-center gap-x-1.5">
            <img
              src="@/assets/images/fortune-wheel-without-border.png"
              class="size-[28px] p-[1.3px]"
            >
            <span class="text-white font-semibold text-base leading-[22px] -tracking-[0.32px]">
              +{{ config.game.fortuneWheel.extraSpin.levelUp.amount }}
            </span>
          </div>
        </div>
      </div>
      <BaseButton
        class="mt-8 w-full"
        @click="shareOnX"
      >
        <template #leftIcon>
          <TwitterIcon />
        </template>
        <T
          key-name="player_level_up.share_on_x"
          default-value="Share on X"
        />
      </BaseButton>
    </div>
  </TheBottomSheet>
</template>

<style scoped>
.circle-ring {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.48) 0%, rgba(255, 255, 255, 0.16) 100%);
}
</style>
